import * as React from 'react'

import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { KeyInputModel } from 'app/models/keyModel'
import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { generatePassword } from 'app/common/passwordHelper'
import { validateEmail } from 'app/common/validationHelper'
import { createServiceUser } from 'app/actions/user'
import { Dropdown } from '@/ui/form/controls/Dropdown'

import { CheckboxField } from '@/modules/shared/components'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import { Button } from '@/ui/button'
import { TextField } from '@/ui/form/controls/TextField'

type Props = {
  companyId: string
  company: CompanyDetailsModel
  roles: RolePermissionModel[]
  onUserAdded: () => void
}

/**
 * Dodanie użytkownika-klucza sprzętowego - funkcyjny odpowiednik
 * `app/components/administration/keys.tsx`.
 *
 * Dwa tryby: kodowanie domyślne (wymaga numeru licencji) i kodowanie klucza
 * sprzętowego (wymaga e-maila i generuje hasło, które trzeba wkleić do konfiguracji).
 */
export const ImportKeyForm: React.FC<Props> = ({
  companyId,
  roles,
  onUserAdded,
}) => {
  const { t } = useLocalize()

  const [hardwareKeyId, setHardwareKeyId] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [license, setLicense] = React.useState('')
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([])
  const [withOffset, setWithOffset] = React.useState(false)
  const [submitted, setSubmitted] = React.useState(false)
  const [keyConfig, setKeyConfig] = React.useState<string | null>(null)

  const errors = {
    hardwareKeyId: Number.isInteger(Number(hardwareKeyId)) && hardwareKeyId
      ? null
      : t('administrationPanel-companies.validation-hardwareKeyId-empty'),
    email:
      !withOffset || validateEmail(email)
        ? null
        : t('administrationPanel-companies.validation-email-invalid'),
    license:
      withOffset || (license != null && license !== '')
        ? null
        : t('administrationPanel-companies.validation-license-empty'),
    role:
      selectedRoles.length === 1
        ? null
        : t('administrationPanel-users.validation-role'),
  }
  const error = (field: keyof typeof errors) =>
    submitted ? errors[field] : null

  const save = async () => {
    setSubmitted(true)
    if (Object.values(errors).some((message) => message != null)) return

    const password = withOffset ? generatePassword() : null
    const payload: KeyInputModel = {
      login: hardwareKeyId,
      email,
      password,
      companyId,
      withOffset,
      license,
      roles: selectedRoles.map(
        (id) => +roles.find((role) => role.id == id).id,
      ),
    }

    const created = await createServiceUser(payload)
    if (created.error == null) {
      if (withOffset) setKeyConfig(btoa(password))
      toast.success({
        title: t('administrationPanel-users.user-added'),
        text: hardwareKeyId,
      })
      onUserAdded()
    } else {
      toast.error({
        title: t('administrationPanel-users.user-not-added'),
        text: hardwareKeyId,
      })
    }
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
      <div className="tw-flex tw-flex-wrap tw-gap-6">
        <CheckboxField
          id="default-coding"
          checked={!withOffset}
          label={t('administrationPanel-users.default-coding')}
          onChange={() => setWithOffset((value) => !value)}
        />
        <CheckboxField
          id="hardware-key-coding"
          checked={withOffset}
          label={t('administrationPanel-users.hardware-key-coding')}
          onChange={() => setWithOffset((value) => !value)}
        />
      </div>

      <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-4">
        <TextField
          fullWidth
          label={t('administrationPanel-users.hardwareKeyId')}
          value={hardwareKeyId}
          onChange={(event) => setHardwareKeyId(event.target.value)}
          error={error('hardwareKeyId')}
        />
        {withOffset && (
          <TextField
            fullWidth
            label={t('administrationPanel-users.email')}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={error('email')}
          />
        )}
        {!withOffset && (
          <TextField
            fullWidth
            label={t('administrationPanel-users.license')}
            value={license}
            onChange={(event) => setLicense(event.target.value)}
            error={error('license')}
          />
        )}
        <div className="tw-flex tw-flex-col tw-gap-1">
          <span className="tw-text-sm tw-font-medium tw-text-gray-700">
            {t('administrationPanel-users.role')}
          </span>
          <div className="drop-wrapper et0-multidrop">
            <Dropdown
              compact
              multiple
              showSelectAll
              placeholder={t('administrationPanel-users.choose-role')}
              options={roles.map((role) => ({
                value: role.id,
                label: role.name,
              }))}
              value={selectedRoles}
              onChange={setSelectedRoles}
            />
          </div>
          {error('role') && (
            <span className="tw-text-xs tw-text-red-500">{error('role')}</span>
          )}
        </div>
      </div>

      {keyConfig && (
        <div>
          <p className="tw-mb-1 tw-text-sm">
            {t('administrationPanel-users.key-config')}
          </p>
          <pre className="tw-rounded tw-bg-base-200 tw-p-2 tw-text-xs">
            <code>
              {'<ro_memory_segment>\n'}
              {'  <offset>20</offset>\n'}
              {`  <content>${keyConfig}</content>\n`}
              {'</ro_memory_segment>'}
            </code>
          </pre>
        </div>
      )}

      <div className="tw-flex tw-justify-end">
        <Button variant="primary" size="sm" onClick={save}>
          {t('operation.add')}
        </Button>
      </div>
    </div>
  )
}
